import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import type { ScoreNode as ScoreNodeType } from "@/lib/pharmacyFirst/types";
import { ClinicalLinkChips } from "./SingleSelectNode";

type ScoreAnswer = {
  selectedIds: string[];
  totalScore: number;
};

interface ScoreNodeProps {
  node: ScoreNodeType;
  isDarkMode?: boolean;
  onAnswer: (answer: ScoreAnswer) => void;
  value?: ScoreAnswer | null;
  stepNumber?: number;
  onOpenCalculator?: (calculatorId: string) => void;
}

export function ScoreNode({
  node,
  isDarkMode = false,
  onAnswer,
  value = null,
  stepNumber,
  onOpenCalculator,
}: ScoreNodeProps) {
  const scoreItems = (node as any).scoreItems || (node as any).items || [];
  const [selected, setSelected] = useState<string[]>(value?.selectedIds || []);

  useEffect(() => {
    setSelected(value?.selectedIds || []);
  }, [node.id, value?.selectedIds?.join("|")]);

  const totalScore = scoreItems.reduce(
    (sum: number, item: any) => sum + (selected.includes(item.id) ? item.points : 0),
    0,
  );

  const handleToggle = (itemId: string) => {
    setSelected((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold shrink-0 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
          {(node as any).hint && (
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {(node as any).hint}
            </p>
          )}
          <p
            className={`text-xs mt-1 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Select each FeverPAIN criterion present.
          </p>
          <ClinicalLinkChips
            links={(node as any).clinicalLinks}
            isDarkMode={isDarkMode}
          />
          {(node as any).linkedCalculatorId && onOpenCalculator && (
            <button
              type="button"
              onClick={() => onOpenCalculator((node as any).linkedCalculatorId)}
              className={`mt-2 text-xs font-medium underline-offset-2 hover:underline ${
                isDarkMode ? "text-indigo-300" : "text-indigo-700"
              }`}
            >
              {(node as any).linkedCalculatorLabel || "Open calculator"}
            </button>
          )}
        </div>
      </div>

      <div
        className={`border rounded-lg p-3 space-y-2 ${
          isDarkMode
            ? "border-gray-600 bg-gray-700"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        {scoreItems.map((item: any) => {
          const checked = selected.includes(item.id);
          return (
            <label
              key={item.id}
              htmlFor={`${node.id}-${item.id}`}
              className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition ${
                checked
                  ? isDarkMode
                    ? "bg-indigo-500/20"
                    : "bg-indigo-50"
                  : isDarkMode
                    ? "hover:bg-gray-600"
                    : "hover:bg-white"
              }`}
            >
              <Checkbox
                id={`${node.id}-${item.id}`}
                checked={checked}
                onCheckedChange={() => handleToggle(item.id)}
                className="mt-0.5"
              />
              <span
                className={`text-sm flex-1 ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {item.text}
              </span>
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  isDarkMode
                    ? "bg-gray-800 text-gray-200"
                    : "bg-white text-gray-700 border border-gray-200"
                }`}
              >
                +{item.points}
              </span>
            </label>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t flex items-center justify-between">
        <div>
          <p
            className={`text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            FeverPAIN score
          </p>
          <p
            className={`text-2xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {totalScore}/5
          </p>
        </div>
        <Button onClick={() => onAnswer({ selectedIds: selected, totalScore })}>
          Continue
        </Button>
      </div>
    </div>
  );
}
